import React, { useState, useMemo } from "react";
import { FeatureCard } from "components/molecules";
import cardData from "assets/json/Features.json";
import personalizedRecommendation from "assets/images/Features/personalizedRecommendation.jpg";
import universityComparison from "assets/images/Features/universityComparison.jpg";
import customizableSearchFilters from "assets/images/Features/customizableSearchFilters.jpg";
import promoteYourInstitution from "assets/images/Features/promoteYourInstitution.jpg";
import attractStudents from "assets/images/Features/attractStudents.jpg";
import listPromoteYourCourses from "assets/images/Features/listPromoteYourCourses.jpg";
import wideDiverseReach from "assets/images/Features/wideDiverseReach.jpg";
import manageStudents from "assets/images/Features/manageStudents.jpg";
import allInOnePlace from "assets/images/Features/allInOnePlace.jpg";
import profile from "assets/images/Features/profile_draft.svg";
import compare from "assets/images/Features/compare_draft.svg";
import search from "assets/images/Features/search_draft.svg";
import "./Features.scss";

interface FeaturesProps {
  type?: React.ReactNode;
  variant?: string;
  isRandom?: boolean;
}

interface Card {
  imageRef: string;
  title: string;
  description: string;
  type: string;
  image: string;
}

type ImagePosition = "left" | "right" | "top" | "bottom";

const imageMap: { [key: string]: string } = {
  profile,
  compare,
  search,
  personalizedRecommendation,
  universityComparison,
  customizableSearchFilters,
  promoteYourInstitution,
  attractStudents,
  listPromoteYourCourses,
  wideDiverseReach,
  manageStudents,
  allInOnePlace,
};

const getSvgFallback = (index: number): string => {
  const fallbackImages = [profile, compare, search];
  return fallbackImages[index % fallbackImages.length];
};

const mappedCardData: Card[] = cardData.map((card, index) => ({
  ...card,
  image: imageMap[card.imageRef] || getSvgFallback(index),
}));

const getRandomVariant = (): "A" | "B" => {
  const storedVariant = localStorage.getItem("featureVariant");

  if (storedVariant === "A" || storedVariant === "B") {
    return storedVariant;
  }

  const variant = Math.random() < 0.5 ? "A" : "B";
  localStorage.setItem("featureVariant", variant);
  return variant;
};

const createImagePositionPattern = (
  length: number,
  pattern: ImagePosition[] = ["left", "right"]
): ImagePosition[] => {
  return Array(length)
    .fill(null)
    .map((_, index) => pattern[index % pattern.length]);
};

const Features: React.FC<FeaturesProps> = ({
  type = "student",
  variant: propVariant = "A",
  isRandom = false,
}) => {
  const initialVariant = isRandom ? getRandomVariant() : propVariant;
  const [activeVariant] = useState(initialVariant);

  const filteredCardData = useMemo(
    () => mappedCardData.filter((card) => card.type === type),
    [type]
  );

  const verticalVariant = () => (
    <div className="cards-container">
      <div className="row">
        {filteredCardData.map((card, index) => (
          <div
            className="col-md-4 p2 my-3"
            key={`vertical-${card.title}-${index}`}
          >
            <FeatureCard
              imageSrc={card.image}
              imageAlt={card.title}
              title={card.title}
              description={card.description}
              className="py-3"
              layout="vertical"
              imagePosition="top"
            />
          </div>
        ))}
      </div>
    </div>
  );

  const horizontalVariant = () => {
    const imagePositions = createImagePositionPattern(filteredCardData.length);

    return (
      <div className="cards-container pt-3">
        {filteredCardData.map((card, index) => (
          <div className="row w-100" key={`horizontal-${card.title}-${index}`}>
            <FeatureCard
              imageSrc={card.image}
              imageAlt={card.title}
              title={card.title}
              description={card.description}
              className="h-100 py-3"
              layout="horizontal"
              imagePosition={imagePositions[index]}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderedContent = useMemo(() => {
    switch (activeVariant) {
      case "A":
        return verticalVariant();
      case "B":
        return horizontalVariant();
      default:
        return verticalVariant();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVariant, filteredCardData]);

  return (
    <section>
      <div className="continer-fluid features-container-background py-5">
        <div
          className="container features-container"
          data-testid="features"
          data-variant={activeVariant}
        >
          {/* <div className="row justify-content-center text-center p-2">
            <div className="col-md-12">
              <hr className="w-50 mx-auto" />
              <h2 className="feature-title">
                Our <mark>Featured</mark> Services
              </h2>
              <hr className="w-25 mx-auto" />
            </div>
          </div> */}

          {renderedContent}
        </div>
      </div>
    </section>
  );
};

export default Features;
